import React, { FC, memo, useLayoutEffect, useMemo, useState } from 'react'

import { Loader } from '@/components/Loader'
import {
  LogoPreloader,
  Preloader,
  VideoBackground,
} from '@/components/VideoPlayer/styles'
import { VideoControls } from '@/components/VideoPlayer/VideoControls'
import { channelSliderImgWidth } from '@/constants/tiles'
import { useActiveBroadcast } from '@/hooks'
import useStore from '@/store/useStore'
import { createHtmlWidgetUrl, findNearestByImgWidth } from '@/utils/utils'

import { HtmlWidgetWrapper } from './styles'
import { BroadcastHtmlWidgetProps, HtmlWidgetProps } from './types'

const HtmlWidgetRaw: FC<HtmlWidgetProps> = ({
  url,
  isBroadcast = false,
  channelImgUrl,
  htmlWidgetSize,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isFailed, setIsFailed] = useState(false)
  const { isDisabledHtmlWidgets } = useStore(
    (state) => state.common.appSettings,
  )

  useLayoutEffect(() => {
    setIsLoading(true)
    setIsFailed(false)
  }, [url])

  if (!url || isDisabledHtmlWidgets) {
    return null
  }

  const renderLoader = () => {
    if (isBroadcast) {
      return (
        <Preloader>
          <LogoPreloader>
            <img src={channelImgUrl} alt="" />
          </LogoPreloader>
        </Preloader>
      )
    }

    if (isLoading) {
      return <Loader />
    }

    return null
  }

  const htmlWidgetUrl = htmlWidgetSize
    ? createHtmlWidgetUrl(url, htmlWidgetSize)
    : url

  return (
    <HtmlWidgetWrapper $htmlWidgetSize={htmlWidgetSize}>
      <iframe
        className={isLoading || isFailed ? '' : 'isVisible'}
        scrolling="no"
        src={htmlWidgetUrl}
        frameBorder="no"
        loading="lazy"
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false)
          setIsFailed(true)
        }}
      />
      {(isLoading || isFailed) && renderLoader()}
    </HtmlWidgetWrapper>
  )
}

export const HtmlWidget = memo(HtmlWidgetRaw)

const BroadcastHtmlWidgetRaw: FC<BroadcastHtmlWidgetProps> = ({
  src = '',
  withControls = true,
  onExitButton,
  broadcastId = '',
  focusIdPrefix = '',
}) => {
  const { activeChannel, channels } = useActiveBroadcast(broadcastId)
  const { isFullscreen } = useStore((state) => state.player)

  const channelImgUrl = useMemo(
    () =>
      findNearestByImgWidth(
        activeChannel?.image?.sources,
        channelSliderImgWidth,
        { prioritizeSvg: true },
      )?.url,
    [activeChannel?.id],
  )

  return (
    <>
      <HtmlWidget url={src} isBroadcast={true} channelImgUrl={channelImgUrl} />
      {isFullscreen && <VideoBackground />}
      {withControls && (
        <VideoControls
          isFullscreen={isFullscreen}
          showChannels={Number(channels?.length) > 1 && !!activeChannel}
          broadcastId={broadcastId}
          onExitButton={onExitButton}
          sliderData={channels || []}
          focusIdPrefix={focusIdPrefix}
          setIsVideoPlaying={() => {}}
          isHTMLWidget={true}
        />
      )}
    </>
  )
}
export const BroadcastHtmlWidget = memo(BroadcastHtmlWidgetRaw)
